<!--
  HelpDeskInquiry — Pineapple 톤
  ────────────────────────────────────────
  · 비즈니스 로직(API/필터/정렬/페이지네이션/모달) 전부 보존
  · admin-3 문의 트리아지 스타일 (3분할 대신 2분할 — 좌 리스트 / 우 상세는 모달)
  · Bootstrap Modal → Pineapple 모달
-->
<template>
  <div class="p-page">
    <a-page-head title="문의 관리" :breadcrumbs="[{ label: '헬프데스크' }, { label: '문의' }]">
      <template #actions>
        <a-btn kind="primary" size="md" @click="openCreateModal">
          <template #icon>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>
          </template>
          문의하기
        </a-btn>
      </template>
    </a-page-head>

    <!-- 전화 문의 — 게시판 등록 외 전화 상담 경로 안내 -->
    <a-card padding="0" class="p-contact-card">
      <div class="p-contact">
        <div class="p-contact__icon" aria-hidden="true">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
        </div>
        <div class="p-contact__who">
          <div class="p-contact__label">{{ $t('inquiry.contact.title') }}</div>
          <div class="p-contact__person">{{ $t('inquiry.contact.person') }}</div>
          <div class="p-contact__sub">{{ $t('inquiry.contact.sub') }}</div>
        </div>
        <div class="p-contact__info">
          <div class="p-contact__row">
            <span class="p-contact__key">{{ $t('inquiry.contact.telLabel') }}</span>
            <a class="p-contact__tel" :href="contactTelHref">{{ $t('inquiry.contact.tel') }}</a>
          </div>
          <div class="p-contact__row">
            <span class="p-contact__key">{{ $t('inquiry.contact.hoursLabel') }}</span>
            <span class="p-contact__value">{{ $t('inquiry.contact.hours') }}</span>
          </div>
        </div>
      </div>
    </a-card>

    <!-- KPI -->
    <div class="p-grid p-grid--kpi">
      <a-card padding="20"><a-stat label="전체 문의" :value="String(inquiries.length)" /></a-card>
      <a-card padding="20"><a-stat label="대기 중"  :value="String(pendingCount)"    :delta="pendingCount ? '처리 필요' : ''" delta-kind="err" /></a-card>
      <a-card padding="20"><a-stat label="처리 중"  :value="String(processingCount)" /></a-card>
      <a-card padding="20"><a-stat label="답변 완료" :value="String(completedCount)" /></a-card>
    </div>

    <!-- 검색/필터 -->
    <a-card title="검색 & 필터" padding="16">
      <div class="p-filter-row">
        <div class="p-field p-field--wide">
          <label class="p-field__label">검색어</label>
          <input class="p-input" type="text" v-model="searchQuery" @input="filterInquiries" placeholder="제목, 내용, 이름 검색..." />
        </div>
        <div class="p-field">
          <label class="p-field__label">카테고리</label>
          <select class="p-input" v-model="selectedCategory" @change="filterInquiries">
            <option value="">전체</option>
            <option v-for="c in categories" :key="c.value" :value="c.value">{{ $t(c.labelKey) }}</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">상태</label>
          <select class="p-input" v-model="statusFilter" @change="filterInquiries">
            <option value="">전체</option>
            <option value="대기">대기</option>
            <option value="처리중">처리중</option>
            <option value="답변완료">답변완료</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">우선순위</label>
          <select class="p-input" v-model="priorityFilter" @change="filterInquiries">
            <option value="">전체</option>
            <option value="high">높음</option>
            <option value="normal">보통</option>
            <option value="low">낮음</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label">정렬</label>
          <select class="p-input" v-model="sortBy" @change="filterInquiries">
            <option value="latest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="priority">우선순위순</option>
          </select>
        </div>
      </div>
    </a-card>

    <!-- 리스트 -->
    <a-card title="문의 리스트" :sub="`${filteredInquiries.length}건`" padding="0" class="p-mt-16">
      <div v-if="loading" class="p-loader"><div class="p-spinner" /></div>
      <a-empty v-else-if="!filteredInquiries.length" title="검색 결과가 없습니다" />
      <div v-else class="p-inquiry-list">
        <div
          v-for="inq in paginatedInquiries"
          :key="inq.id"
          class="p-inquiry"
          :class="{ 'is-urgent': inq.priority === 'high' || inq.priority === 'urgent' }"
          @click="viewInquiry(inq)"
        >
          <div class="p-inquiry__head">
            <a-pill kind="yellow">{{ getCategoryLabel(inq.category) }}</a-pill>
            <a-pill :kind="statusKind(inq.status)">{{ inq.status }}</a-pill>
            <a-pill :kind="priorityKind(inq.priority)">{{ getPriorityLabel(inq.priority) }}</a-pill>
          </div>
          <div class="p-inquiry__title">{{ inq.subject }}</div>
          <div class="p-inquiry__body">{{ inq.content }}</div>
          <div class="p-inquiry__meta">
            <span>👤 {{ inq.user_name }}</span>
            <span class="p-mono">{{ inq.user_email }}</span>
            <span class="p-mono">{{ formatDate(inq.created_at) }}</span>
          </div>
        </div>
      </div>

      <!-- 페이지네이션 -->
      <div v-if="filteredInquiries.length > itemsPerPage" class="p-pagination">
        <button class="p-page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">‹</button>
        <button
          v-for="p in totalPages"
          :key="p"
          class="p-page-btn"
          :class="{ 'is-active': currentPage === p }"
          @click="changePage(p)"
        >{{ p }}</button>
        <button class="p-page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">›</button>
      </div>
    </a-card>

    <!-- 상세 모달 -->
    <div v-if="showDetailModal" class="p-modal-backdrop" @click.self="closeDetailModal">
      <div class="p-modal">
        <div class="p-modal__head">
          <div>
            <div class="p-modal__title">문의 상세보기</div>
            <div class="p-modal__sub">{{ selectedInquiry?.user_name }}님의 문의</div>
          </div>
          <button class="p-icon-btn" @click="closeDetailModal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="p-modal__body">
          <template v-if="selectedInquiry">
            <div class="p-inquiry-detail">
              <div class="p-inquiry__head">
                <a-pill kind="yellow">{{ getCategoryLabel(selectedInquiry.category) }}</a-pill>
                <a-pill :kind="statusKind(selectedInquiry.status)">{{ selectedInquiry.status }}</a-pill>
                <a-pill :kind="priorityKind(selectedInquiry.priority)">{{ getPriorityLabel(selectedInquiry.priority) }}</a-pill>
              </div>
              <h4 class="p-inquiry-detail__title">{{ selectedInquiry.subject }}</h4>
              <p class="p-inquiry-detail__content">{{ selectedInquiry.content }}</p>
              <div class="p-inquiry__meta">
                <span>👤 {{ selectedInquiry.user_name }}</span>
                <span class="p-mono">{{ selectedInquiry.user_email }}</span>
                <span class="p-mono">{{ formatDate(selectedInquiry.created_at) }}</span>
              </div>
            </div>

            <!-- 상태값이 한글('처리중')로 내려와 영문 'processing' 비교가 항상 false 였음 — 답변 존재 여부로만 판단 -->
            <div v-if="selectedInquiry.reply" class="p-reply">
              <div class="p-reply__label">✓ 답변 내용</div>
              <div class="p-reply__body">{{ selectedInquiry.reply }}</div>
              <div class="p-reply__date">답변 일시: {{ formatDate(selectedInquiry.reply_date) }}</div>
            </div>
            <div v-else class="p-alert">
              <div class="p-alert__title">대기 중</div>
              <div class="p-alert__list" style="padding-left: 0;">아직 답변이 등록되지 않았습니다. 답변이 등록되면 이메일로 알려드립니다.</div>
            </div>
          </template>
        </div>
        <div class="p-modal__foot">
          <a-btn kind="ghost" @click="closeDetailModal">{{ $t('btn.28') }}</a-btn>
        </div>
      </div>
    </div>

    <!-- 생성 모달 -->
    <div v-if="showCreateModal" class="p-modal-backdrop" @click.self="closeCreateModal">
      <div class="p-modal">
        <div class="p-modal__head">
          <div>
            <div class="p-modal__title">문의하기</div>
            <div class="p-modal__sub">궁금하신 사항을 문의해 주세요</div>
          </div>
          <button class="p-icon-btn" @click="closeCreateModal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <form class="p-modal__body p-form" @submit.prevent="submitInquiry">
          <!-- 글 작성이 어려우면 전화로도 문의 가능 -->
          <div class="p-contact-note">
            <div class="p-contact-note__head">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              <span>{{ $t('inquiry.contact.title') }}</span>
            </div>
            <div class="p-contact-note__person">{{ $t('inquiry.contact.person') }}</div>
            <div class="p-contact-note__row">
              <span class="p-contact__key">{{ $t('inquiry.contact.telLabel') }}</span>
              <a class="p-contact__tel" :href="contactTelHref">{{ $t('inquiry.contact.tel') }}</a>
            </div>
            <div class="p-contact-note__row">
              <span class="p-contact__key">{{ $t('inquiry.contact.hoursLabel') }}</span>
              <span class="p-contact__value">{{ $t('inquiry.contact.hours') }}</span>
            </div>
          </div>
          <div class="p-form__row">
            <div class="p-form__field">
              <label class="p-form__label">카테고리 <span class="p-form__required">*</span></label>
              <select v-model="createForm.category" class="p-input" required>
                <option value="">선택하세요</option>
                <option v-for="c in categories" :key="c.value" :value="c.value">{{ $t(c.labelKey) }}</option>
              </select>
            </div>
            <div class="p-form__field">
              <label class="p-form__label">우선순위 <span class="p-form__required">*</span></label>
              <select v-model="createForm.priority" class="p-input" required>
                <option value="low">낮음</option>
                <option value="medium">보통</option>
                <option value="high">높음</option>
                <option value="urgent">긴급</option>
              </select>
            </div>
          </div>
          <div class="p-form__field">
            <label class="p-form__label">제목 <span class="p-form__required">*</span></label>
            <input v-model="createForm.subject" type="text" class="p-input" placeholder="문의 제목을 입력하세요" required />
          </div>
          <div class="p-form__field">
            <label class="p-form__label">내용 <span class="p-form__required">*</span></label>
            <textarea v-model="createForm.content" class="p-input p-input--area" rows="8" placeholder="문의 내용을 상세히 입력해주세요" required />
          </div>
          <div class="p-modal__foot p-modal__foot--inline">
            <a-btn kind="ghost" type="button" @click="closeCreateModal">취소</a-btn>
            <a-btn kind="primary" type="submit">문의 등록</a-btn>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import AStat from '@/components/Pineapple/AStat.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';

export default {
  name: 'HelpDeskInquiry',
  components: { APageHead, ACard, AStat, APill, ABtn, AEmpty },
  data() {
    return {
      inquiries: [],
      filteredInquiries: [],
      categories: [
        { value: 'general',   labelKey: 'inquiry.helpdesk_category.general' },
        { value: 'payment',   labelKey: 'inquiry.helpdesk_category.payment' },
        { value: 'technical', labelKey: 'inquiry.helpdesk_category.technical' },
        { value: 'account',   labelKey: 'inquiry.helpdesk_category.account' },
        { value: 'course',    labelKey: 'inquiry.helpdesk_category.course' },
        { value: 'other',     labelKey: 'inquiry.helpdesk_category.other' },
      ],
      loading: false,
      searchQuery: '',
      selectedCategory: '',
      statusFilter: '',
      priorityFilter: '',
      sortBy: 'latest',
      currentPage: 1,
      itemsPerPage: 10,
      selectedInquiry: null,
      showDetailModal: false,
      showCreateModal: false,
      createForm: {
        category: '', subject: '', content: '',
        priority: 'medium', user_name: '', user_email: '',
      },
      selectedFiles: [],
      // 표기(070-8798-7345 / +82-70-8798-7345)는 로케일별, 걸리는 번호는 국제표기로 통일
      contactTelHref: 'tel:+827087987345',
    };
  },
  computed: {
    isLoggedIn() { return !!this.$store.state.auth.token; },
    pendingCount()    { return this.inquiries.filter(i => i.status === '대기').length; },
    processingCount() { return this.inquiries.filter(i => i.status === '처리중').length; },
    completedCount()  { return this.inquiries.filter(i => i.status === '답변완료').length; },
    totalPages()  { return Math.ceil(this.filteredInquiries.length / this.itemsPerPage); },
    paginatedInquiries() {
      const s = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredInquiries.slice(s, s + this.itemsPerPage);
    },
  },
  mounted() { this.loadInquiries(); },
  methods: {
    statusKind(s) {
      if (s === '대기')    return 'warn';
      if (s === '처리중')  return 'info';
      if (s === '답변완료') return 'ok';
      return 'default';
    },
    priorityKind(p) {
      if (p === 'high' || p === 'urgent') return 'err';
      if (p === 'medium')                 return 'warn';
      return 'default';
    },

    async loadInquiries() {
      this.loading = true;
      try {
        const r = await this.$axios({ method: 'get', url: '/helpdesk/inquiries', loadingBar: true });
        this.inquiries = r.data.inquiries || [];
        this.filterInquiries();
      } catch (e) {
        console.error('문의 로딩 실패:', e);
        this.$swal({ icon: 'error', title: '로딩 실패', text: '문의 목록을 불러오는데 실패했습니다.' });
      } finally {
        this.loading = false;
      }
    },

    filterInquiries() {
      let filtered = [...this.inquiries];
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        filtered = filtered.filter(i =>
          i.subject.toLowerCase().includes(q) ||
          i.content.toLowerCase().includes(q) ||
          i.user_name.toLowerCase().includes(q),
        );
      }
      if (this.selectedCategory) filtered = filtered.filter(i => i.category === this.selectedCategory);
      if (this.statusFilter) filtered = filtered.filter(i => i.status === this.statusFilter);
      if (this.priorityFilter) filtered = filtered.filter(i => i.priority === this.priorityFilter);
      if (this.sortBy === 'latest')   filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      if (this.sortBy === 'oldest')   filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      if (this.sortBy === 'priority') {
        const order = { high: 0, normal: 1, low: 2 };
        filtered.sort((a, b) => order[a.priority] - order[b.priority]);
      }
      this.filteredInquiries = filtered;
      this.currentPage = 1;
    },

    changePage(p) {
      if (p >= 1 && p <= this.totalPages) this.currentPage = p;
    },
    viewInquiry(inq) {
      this.selectedInquiry = inq;
      this.showDetailModal = true;
    },
    closeDetailModal() {
      this.showDetailModal = false;
      this.selectedInquiry = null;
    },
    formatDate(s) {
      if (!s) return '-';
      return new Date(s).toLocaleDateString('ko-KR', {
        year: 'numeric', month: 'long', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    },
    getCategoryLabel(v) {
      const c = this.categories.find(x => x.value === v);
      return c ? this.$t(c.labelKey) : v;
    },
    getPriorityLabel(p) {
      return ({ low: '낮음', medium: '보통', high: '높음', urgent: '긴급' })[p] || p;
    },

    openCreateModal() {
      const u = this.$store.state.auth.user_info || {};
      this.createForm = {
        category: '', subject: '', content: '', priority: 'medium',
        user_name: this.isLoggedIn ? (u.name || '') : '',
        user_email: this.isLoggedIn ? (u.email || '') : '',
      };
      this.selectedFiles = [];
      this.showCreateModal = true;
    },
    closeCreateModal() { this.showCreateModal = false; },

    async submitInquiry() {
      try {
        const formData = new FormData();
        formData.append('category', this.createForm.category);
        formData.append('subject', this.createForm.subject);
        formData.append('content', this.createForm.content);
        formData.append('priority', this.createForm.priority);
        if (!this.isLoggedIn) {
          formData.append('user_name', this.createForm.user_name);
          formData.append('user_email', this.createForm.user_email);
        }
        for (const f of this.selectedFiles) formData.append('attachments', f);

        const config = {
          method: 'post', url: '/helpdesk/inquiries', data: formData, loadingBar: true,
        };
        if (this.isLoggedIn) {
          config.headers = { Authorization: `Bearer ${this.$store.state.auth.token}` };
        }
        await this.$axios(config);
        this.$swal({ icon: 'success', title: '문의 등록 완료', text: '문의가 성공적으로 등록되었습니다' });
        this.closeCreateModal();
        this.loadInquiries();
      } catch (e) {
        console.error('문의 등록 실패:', e);
        this.$swal({
          icon: 'error', title: '등록 실패',
          text: e.response?.data?.message || '문의 등록에 실패했습니다',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-page {
  font-family: $p-font-body;
  color: $p-ink;
}
.p-mt-16 { margin-top: 16px; }
.p-mono { font-family: $p-font-mono; font-size: 11px; color: $p-ink2; }

.p-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 16px;

  &--kpi { grid-template-columns: repeat(4, 1fr); }
  @media (max-width: 1100px) { &--kpi { grid-template-columns: repeat(2, 1fr); } }
}

.p-filter-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
  gap: 12px;
  @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
}
.p-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;

  &--wide { /* uses grid template column */ }

  &__label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $p-ink-soft;
  }
}
.p-input {
  width: 100%;
  height: 36px;
  padding: 0 12px;
  border: 1px solid $p-line;
  border-radius: 8px;
  background: $p-panel;
  font-family: $p-font-body;
  font-size: 12px;
  color: $p-ink;
  outline: none;
  &:focus { border-color: $p-point; box-shadow: 0 0 0 3px rgba(255,201,60,0.2); }
  &--area { height: auto; padding: 10px 12px; resize: vertical; min-height: 120px; }
}

.p-loader { padding: 60px 0; text-align: center; }
.p-spinner {
  display: inline-block;
  width: 36px; height: 36px;
  border: 3px solid $p-line; border-top-color: $p-point;
  border-radius: 50%;
  animation: p-spin 0.8s linear infinite;
}
@keyframes p-spin { to { transform: rotate(360deg); } }

.p-inquiry-list {
  display: flex;
  flex-direction: column;
}
.p-inquiry {
  padding: 16px 20px;
  border-bottom: 1px solid $p-line-soft;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;

  /* 카드 안의 흰 배경과 페이지 베이지 배경 양쪽 모두와 명확히 구분되는
     옅은 노란 hover — 페이지 배경($p-bg)과 절대 같지 않게 함 */
  &:hover { background: $p-point-soft; }
  &:last-child { border-bottom: 0; }
  &.is-urgent { border-left: 3px solid $p-err; padding-left: 17px; }

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
  }
  &__title {
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
    margin-bottom: 4px;
  }
  &__body {
    font-size: 12px;
    color: $p-ink-mid;
    line-height: 1.5;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  &__meta {
    display: flex;
    gap: 14px;
    font-size: 11px;
    color: $p-ink-soft;
  }
}

.p-pagination {
  display: flex;
  justify-content: center;
  gap: 4px;
  padding: 16px;
  border-top: 1px solid $p-line;
}
.p-page-btn {
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid $p-line;
  background: $p-panel;
  border-radius: 6px;
  font-family: $p-font-mono;
  font-size: 12px;
  color: $p-ink2;
  cursor: pointer;
  &:hover:not(:disabled) { background: $p-bg; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &.is-active { background: $p-ink; color: #fff; border-color: $p-ink; }
}

/* 모달 */
.p-modal-backdrop {
  position: fixed; inset: 0;
  background: rgba(31, 27, 20, 0.5);
  display: grid; place-items: center;
  z-index: 1050;
}
.p-modal {
  background: $p-panel;
  border-radius: 16px;
  width: 90vw; max-width: 720px;
  max-height: 85vh;
  display: flex; flex-direction: column;
  box-shadow: 0 24px 48px -16px rgba(0,0,0,0.32);

  &__head {
    padding: 16px 20px;
    border-bottom: 1px solid $p-line;
    display: flex; justify-content: space-between; align-items: center;
    gap: 12px;
  }
  &__title { font-size: 15px; font-weight: 800; color: $p-ink; }
  &__sub { font-size: 11px; color: $p-ink-soft; margin-top: 2px; }
  &__body { flex: 1; overflow: auto; padding: 20px; }
  &__foot {
    padding: 14px 20px;
    border-top: 1px solid $p-line;
    background: $p-panel-alt;
    display: flex; justify-content: flex-end; gap: 8px;

    &--inline { margin: 16px -20px -20px; }
  }
}

.p-icon-btn {
  width: 28px; height: 28px;
  border-radius: 6px; border: 0;
  background: transparent; color: $p-ink-mid;
  cursor: pointer;
  display: inline-grid; place-items: center;
  &:hover { background: $p-bg; color: $p-ink; }
}

.p-inquiry-detail {
  background: $p-panel-alt;
  border: 1px solid $p-line;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;

  &__title {
    font-size: 16px;
    font-weight: 900;
    color: $p-ink;
    margin: 8px 0 10px;
  }
  &__content {
    font-size: 13px;
    line-height: 1.6;
    color: $p-ink2;
    white-space: pre-line;
    margin: 0 0 12px;
  }
}

.p-reply {
  padding: 16px;
  background: $p-ok-soft;
  border-left: 3px solid $p-ok;
  border-radius: 8px;

  &__label { font-size: 11px; font-weight: 800; color: $p-ok; margin-bottom: 6px; }
  &__body { font-size: 13px; color: $p-ink; white-space: pre-line; }
  &__date { margin-top: 8px; font-size: 11px; color: $p-ink-soft; }
}

.p-alert {
  padding: 12px 14px;
  background: $p-info-soft;
  border: 1px solid #C8DFF5;
  border-radius: 8px;

  &__title { font-size: 12px; font-weight: 800; color: $p-info; margin-bottom: 4px; }
  &__list { font-size: 12px; color: $p-ink-mid; line-height: 1.5; }
}

/* 전화 문의 안내 */
.p-contact-card { margin-bottom: 16px; }
.p-contact {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(90deg, $p-point-soft 0%, $p-panel 60%);
  border-left: 3px solid $p-point;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  &__icon {
    flex: none;
    width: 40px; height: 40px;
    display: grid; place-items: center;
    border-radius: 10px;
    background: $p-point;
    color: $p-ink;
  }
  &__who { flex: 1; min-width: 0; }
  &__label {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $p-point-deep;
    margin-bottom: 3px;
  }
  &__person {
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
  }
  &__sub {
    font-size: 11px;
    color: $p-ink-mid;
    margin-top: 3px;
  }
  &__info {
    flex: none;
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: right;

    @media (max-width: 900px) { text-align: left; }
  }
  &__row {
    display: flex;
    align-items: baseline;
    justify-content: flex-end;
    gap: 8px;

    @media (max-width: 900px) { justify-content: flex-start; }
  }
  &__key {
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: $p-ink-soft;
  }
  &__value { font-size: 12px; color: $p-ink2; }
  &__tel {
    font-family: $p-font-mono;
    font-size: 15px;
    font-weight: 700;
    color: $p-ink;
    text-decoration: none;
    &:hover { color: $p-point-deep; text-decoration: underline; }
  }
}

.p-contact-note {
  padding: 12px 14px;
  background: $p-point-soft;
  border: 1px solid $p-line;
  border-radius: 8px;

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: $p-point-deep;
    margin-bottom: 6px;
  }
  &__person {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    margin-bottom: 4px;
  }
  &__row {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 2px;
  }
}

.p-form {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  &__field { display: flex; flex-direction: column; gap: 6px; }
  &__label { font-size: 12px; font-weight: 700; color: $p-ink; }
  &__required { color: $p-err; }
}
</style>
